import { ReactElement, useCallback, useEffect, useState } from 'react'
import { Stack, Typography } from '@mui/material'
import AlignmentHelper from '@editor/pages/StructuralPlanning/components/AlignmentHelper'
import { useVerticalOpeningHelpers } from '@editor/pages/StructuralPlanning/hooks/useVerticalOpeningHelpers'
import { CorneredSection } from '@ui2/structure'
import { SwitchLabeled } from '@ui/actions'
import { useControlStore, useControlStoreApi, useModelStore, useSceneStore } from '@editorStores'
import { useCameraEvent, useScopedSelectionReset } from '@editorHooks'
import { formatPointerModifier } from 'src/shortcuts/pointerModifiers'
import useIFCOpeningSelection from '../../hooks/useIFCOpeningSelection'
import IFCOpeningMultiEdit from '../IFCOpeningMultiEdit'
import OpeningPlaneList from './OpeningPlaneList'
import { DetectedOpeningPlane, OPENING_ALIGN_BRACKET_ID } from './utils'

/**
 * "Öffnungen ausrichten" step: lets the user select wall openings in the scene and adjust their
 * coordinates (reusing the Structural Planning opening editor) so openings stacked above each other
 * share the same coordinates. Uses a clip bracket ("Schnittklammer") to compare them.
 */
const IFCOpeningAlignPanel = (): ReactElement => {
  const controlStoreApi = useControlStoreApi()
  const setGlbTypeVisibility = useControlStore(state => state.setGlbTypeVisibility)
  const setShowGltfModel = useControlStore(state => state.setShowGltfModel)
  const clipPlaneIds = useSceneStore(state => state.clipPlaneIds)
  const setShowVerticalOpeningAlignmentHelpers = useModelStore(
    state => state.setShowVerticalOpeningAlignmentHelpers,
  )

  // The plane the user picked. Kept here (not in the list) so the highlight survives switching to the
  // opening edit form and back. The highlight is only shown while the plane's clip bracket actually
  // exists, so removing the bracket (toolbar reset or scene context menu) drops the highlight.
  const [selectedPlaneId, setSelectedPlaneId] = useState<string | null>(null)
  const activePlaneId = clipPlaneIds.includes(OPENING_ALIGN_BRACKET_ID) ? selectedPlaneId : null

  // "Kamera ausrichten" tool switch: selecting a plane also turns the camera to face it.
  const [moveCameraToPlane, setMoveCameraToPlane] = useState(true)

  // Camera framing of the last selected plane. Held here (not in the plane list) so the camera does
  // not reset when the list unmounts because an opening got selected for editing. Only registered
  // while the switch is on and the scene is in the perspective view (a selected storey means the
  // orthographic floorplan view, which a perspective event must not override).
  const storey = useControlStore(state => state.floorplanState.storey)
  const [cameraEvent, setCameraEvent] = useState<OrientedFitPerspectiveCameraEvent | null>(null)
  useCameraEvent(moveCameraToPlane && storey === null ? cameraEvent : null)

  const handleSelectPlane = useCallback((plane: DetectedOpeningPlane) => {
    setSelectedPlaneId(plane.id)
    // A fresh event object even for the same plane, so re-clicking re-frames the camera.
    setCameraEvent({
      cameraType: 'perspective',
      type: 'orientedFitPerspective',
      target: [plane.frame.center.x, plane.frame.center.y, plane.frame.center.z],
      right: [plane.frame.right.x, plane.frame.right.y, plane.frame.right.z],
      up: [plane.frame.up.x, plane.frame.up.y, plane.frame.up.z],
      size: { width: plane.frame.width, height: plane.frame.height },
      priority: 2,
    })
  }, [])

  useIFCOpeningSelection(true)

  // The vertical opening alignment helper lines register themselves into the scene while mounted.
  const verticalOpeningHelpers = useVerticalOpeningHelpers()

  // Clear any selection carried over from a previous step, and on leave.
  useScopedSelectionReset()

  // On entering the step, show the IFC "Öffnungen" type (as in the storey controls) and the vertical
  // opening alignment helpers ("Ausrichtungshilfe").
  useEffect(() => {
    setGlbTypeVisibility('openings', true)
    setShowVerticalOpeningAlignmentHelpers(true)
    return () => {
      setShowVerticalOpeningAlignmentHelpers(false)
    }
  }, [setGlbTypeVisibility, setShowVerticalOpeningAlignmentHelpers])

  // Hide the IFC GLTF model while aligning openings so the clip bracket view is not obscured, and
  // restore its previous visibility when leaving the step.
  useEffect(() => {
    const showGltfModelOnEnter = controlStoreApi.getState().showGltfModel
    setShowGltfModel(false)
    return () => {
      setShowGltfModel(showGltfModelOnEnter)
    }
  }, [controlStoreApi, setShowGltfModel])

  return (
    <Stack spacing={2}>
      <AlignmentHelper verticalOpeningHelpers={verticalOpeningHelpers} />

      <IFCOpeningMultiEdit
        emptyState={
          <CorneredSection>
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Legen Sie eine Schnittklammer an und prüfen Sie, ob übereinanderstehende Öffnungen
                die gleichen Koordinaten haben. Wählen Sie eine oder mehrere Öffnungen (mit{' '}
                {formatPointerModifier('multiSelect')}) im Modell aus, um ihre Koordinaten
                anzupassen.
              </Typography>

              <OpeningPlaneList activePlaneId={activePlaneId} onSelectPlane={handleSelectPlane} />
            </Stack>
          </CorneredSection>
        }
      />

      <CorneredSection title="Tools" collapsible>
        <SwitchLabeled
          checked={moveCameraToPlane}
          onChange={event => setMoveCameraToPlane(event.target.checked)}
          label="Kamera auf Ebene ausrichten"
          data-cy="switch-opening-align-camera"
        />
      </CorneredSection>
    </Stack>
  )
}

export default IFCOpeningAlignPanel
